import { ImagePlus, Minus, Plus, Trash2, TriangleAlert } from 'lucide-react';
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';

import { Alert } from '@/design-system/ui/Alert';
import { Avatar } from '@/design-system/ui/Avatar';
import { Button } from '@/design-system/ui/Button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader } from '@/design-system/ui/Dialog';
import type { PhotoUpload } from '@escala/contracts';
import { PHOTO_TYPES, photoFileError } from '@escala/domain/photo';
import {
  clampFraming,
  cropOf,
  initialFraming,
  MAX_ZOOM,
  MIN_ZOOM,
  openPhoto,
  renderPhoto,
  scaleOf,
  zoomTo,
  type Framing,
  type PhotoSource,
} from '@/services/photo';

/** Diâmetro do visor do recorte, em px da tela. */
const VIEWPORT = 240;
/** Passo das setas do teclado (px) e dos botões de zoom. */
const PAN_STEP = 10;
const ZOOM_STEP = 0.25;

interface Picked {
  source: PhotoSource;
  /** Para mostrar no visor (revogado ao trocar ou fechar). */
  url: string;
}

export interface PhotoDialogProps {
  /** Para as iniciais, enquanto não há foto. */
  name: string;
  /** A foto atual (o endereço), ou `null`. */
  photo: string | null;
  /** O texto antes de escolher a imagem (o tipo e o tamanho aceitos vêm antes). */
  hint?: string;
  /** Grava a foto já recortada e reduzida; `false` mantém o diálogo aberto. */
  onSave: (upload: PhotoUpload) => Promise<boolean> | boolean;
  /** "Remover foto", quando há foto; sem ele, o botão não aparece. */
  onRemove?: () => Promise<boolean> | boolean;
  onClose: () => void;
}

/**
 * "Enviar foto": escolher a imagem (JPG, PNG ou WebP, até 2 MB), enquadrar no círculo —
 * arrastando (ou com as setas) e com o zoom — e salvar. A imagem é recortada e reduzida no
 * navegador (`services/photo`) e quem abre decide o que fazer com ela: o perfil grava na
 * hora (`actions.setMemberPhoto`), o cadastro público guarda até o envio. Com foto, também
 * "Remover foto".
 */
export default function PhotoDialog({
  name,
  photo,
  hint = 'A foto aparece nas escalas e no perfil.',
  onSave,
  onRemove,
  onClose,
}: PhotoDialogProps) {
  const input = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<Picked | null>(null);
  const [framing, setFraming] = useState<Framing>({ zoom: MIN_ZOOM, x: 0, y: 0 });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const drag = useRef<{ pointer: number; x: number; y: number; from: Framing } | null>(null);

  useEffect(() => () => (picked ? URL.revokeObjectURL(picked.url) : undefined), [picked]);

  const choose = async (file: File | undefined) => {
    if (!file) return;

    const fileError = photoFileError(file);

    if (fileError) {
      setError(fileError);

      return;
    }

    const source = await openPhoto(file);

    if (!source) {
      setError('Não foi possível abrir esta imagem. Tente outra.');

      return;
    }

    setError('');
    setPicked({ source, url: URL.createObjectURL(file) });
    setFraming(initialFraming(source, VIEWPORT));
  };

  const frame = (next: Framing) => picked && setFraming(clampFraming(picked.source, VIEWPORT, next));
  const zoom = (value: number) => picked && setFraming(zoomTo(picked.source, VIEWPORT, framing, value));

  const save = async () => {
    if (!picked) return;

    setBusy(true);

    const upload = await renderPhoto(picked.source, cropOf(picked.source, VIEWPORT, framing));

    if (!upload) {
      setBusy(false);
      setError('Não foi possível preparar a foto neste navegador. Tente outra imagem.');

      return;
    }
    if (await onSave(upload)) onClose();
    else setBusy(false);
  };

  const remove = async () => {
    if (!onRemove) return;
    setBusy(true);
    if (await onRemove()) onClose();
    else setBusy(false);
  };

  const onPointerDown = (e: PointerEvent<HTMLButtonElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { pointer: e.pointerId, x: e.clientX, y: e.clientY, from: framing };
  };
  const onPointerMove = (e: PointerEvent<HTMLButtonElement>) => {
    const d = drag.current;

    if (!d || d.pointer !== e.pointerId) return;
    frame({ zoom: d.from.zoom, x: d.from.x + e.clientX - d.x, y: d.from.y + e.clientY - d.y });
  };
  const onPointerUp = () => {
    drag.current = null;
  };
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [PAN_STEP, 0],
      ArrowRight: [-PAN_STEP, 0],
      ArrowUp: [0, PAN_STEP],
      ArrowDown: [0, -PAN_STEP],
    };
    const move = moves[e.key];

    if (move) {
      e.preventDefault();
      frame({ ...framing, x: framing.x + move[0], y: framing.y + move[1] });
    } else if (e.key === '+' || e.key === '=') {
      zoom(framing.zoom + ZOOM_STEP);
    } else if (e.key === '-') {
      zoom(framing.zoom - ZOOM_STEP);
    }
  };

  const scale = picked ? scaleOf(picked.source, VIEWPORT, framing.zoom) : 1;

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent described className="w-[min(440px,calc(100vw-24px))]">
        <DialogHeader
          title={photo ? 'Trocar foto' : 'Enviar foto'}
          description={
            picked
              ? 'Arraste para enquadrar e use o zoom. A foto aparece no círculo.'
              : `JPG, PNG ou WebP, até 2 MB. ${hint}`
          }
        />
        <DialogBody className="items-center gap-4">
          {picked ? (
            <>
              <button
                type="button"
                aria-roledescription="enquadramento"
                aria-label="Enquadramento da foto. Arraste ou use as setas para mover, e + ou - para o zoom."
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onKeyDown={onKeyDown}
                style={{ width: VIEWPORT, height: VIEWPORT }}
                className="relative block shrink-0 cursor-grab touch-none overflow-hidden rounded-pill bg-surface-muted ring-2 ring-line-input select-none active:cursor-grabbing"
              >
                <img
                  src={picked.url}
                  alt=""
                  draggable={false}
                  style={{
                    width: picked.source.width * scale,
                    height: picked.source.height * scale,
                    transform: `translate(${framing.x}px, ${framing.y}px)`,
                  }}
                  className="pointer-events-none absolute top-0 left-0 max-w-none origin-top-left"
                />
              </button>
              <div className="flex w-full max-w-70 items-center gap-2">
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label="Diminuir o zoom"
                  disabled={framing.zoom <= MIN_ZOOM}
                  onClick={() => zoom(framing.zoom - ZOOM_STEP)}
                >
                  <Minus aria-hidden />
                </Button>
                <input
                  type="range"
                  aria-label="Zoom"
                  min={MIN_ZOOM}
                  max={MAX_ZOOM}
                  step={0.01}
                  value={framing.zoom}
                  onChange={(e) => zoom(Number(e.target.value))}
                  className="min-w-0 flex-1 accent-primary"
                />
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label="Aumentar o zoom"
                  disabled={framing.zoom >= MAX_ZOOM}
                  onClick={() => zoom(framing.zoom + ZOOM_STEP)}
                >
                  <Plus aria-hidden />
                </Button>
              </div>
            </>
          ) : (
            <Avatar name={name.trim() || 'Novo membro'} photo={photo} size="3xl" className="size-30" />
          )}

          {error ? (
            <Alert tone="danger" className="w-full">
              <TriangleAlert aria-hidden />
              <span>{error}</span>
            </Alert>
          ) : null}

          <input
            ref={input}
            type="file"
            accept={PHOTO_TYPES.join(',')}
            aria-label="Escolher imagem"
            className="sr-only"
            tabIndex={-1}
            onChange={(e) => {
              void choose(e.target.files?.[0]);
              // Escolher o mesmo arquivo de novo também dispara.
              e.target.value = '';
            }}
          />
        </DialogBody>
        <DialogFooter className="flex-wrap">
          {!picked && photo && onRemove ? (
            <Button variant="destructive" className="mr-auto" disabled={busy} onClick={() => void remove()}>
              <Trash2 aria-hidden />
              Remover foto
            </Button>
          ) : null}
          <Button variant="secondary" disabled={busy} onClick={onClose}>
            Cancelar
          </Button>
          {picked ? (
            <>
              <Button variant="secondary" disabled={busy} onClick={() => input.current?.click()}>
                Trocar imagem
              </Button>
              <Button disabled={busy} onClick={() => void save()}>
                {busy ? 'Enviando…' : 'Salvar foto'}
              </Button>
            </>
          ) : (
            <Button disabled={busy} onClick={() => input.current?.click()}>
              <ImagePlus aria-hidden />
              Escolher imagem
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
